import { Component, Inject, InjectionToken } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject } from 'rxjs';

import { MortgagesService } from '../../../modules/mortgages/mortgages.service';
import { fadeAnimation, sidepaneMove } from '../../../shared/animations/sidepane-animation';
import { ToastService } from '../../../../../../projects/client/src/app/shared/services';
import { TenantConfigService } from '../../../../../../tenant-config/tenant-config.service';
import { tenantApprovalText } from '../../../utils/variables.data';

export interface ApproveComplianceConfig {
  backPath: string;
}

export interface ApproveComplianceService {
  approveComplianceHandover(paramMap: ParamMap, body: { value: string }): Promise<void>;

  approve$?: Subject<void>;
}

export const APPROVE_COMPLIANCE_SERVICE = new InjectionToken<ApproveComplianceService>('APPROVE_COMPLIANCE_SERVICE');

@Component({
  selector: 'hf-approve-compliance',
  templateUrl: './approve-compliance.component.html',
  styleUrls: ['./approve-compliance.component.scss', '../../../styles/components/sidepanes.partial.scss'],
  animations: [sidepaneMove, fadeAnimation],
  host: { '[@sidepaneMove]': 'true' }
})
export class ApproveComplianceComponent {
  public get mortgageId() {
    return this.route.snapshot.paramMap.get('id');
  }

  public get config() {
    return this.route.snapshot.data as ApproveComplianceConfig;
  }

  public form = new UntypedFormGroup({
    value: new UntypedFormControl(undefined)
  });

  public isSubmitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    @Inject(APPROVE_COMPLIANCE_SERVICE) private approveComplianceService: ApproveComplianceService,
    private mortgagesService: MortgagesService,
    private toasterService: ToastService,
    public readonly tenantConfigService: TenantConfigService
  ) {}

  public async onSubmit() {
    const value = this.form.value;

    try {
      this.isSubmitting = true;

      await this.approveComplianceService.approveComplianceHandover(this.route.snapshot.paramMap, value);

      this.approveComplianceService.approve$?.next();
      this.mortgagesService.reloadMortgageDashboard.next();

      this.toasterService.add(
        { header: tenantApprovalText('Compliance approved.', this.tenantConfigService.complianceApprovalName) },
        'success'
      );

      this.onBack();
    } catch (err) {
      this.toasterService.add(
        {
          header: 'Error',
          message: ` Please try again. ${err.error.error.message}`
        },
        'error'
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  public onBack() {
    const path = this.config.backPath || '..';
    this.router.navigate([path], { relativeTo: this.route });
  }
}
